'use client'

import { type HTMLMotionProps, motion } from 'motion/react'
import { forwardRef, type MouseEvent, type PropsWithChildren } from 'react'

import { cn } from '@mntn-dev/ui-utilities'

import type { UseWidthProps } from '../../hooks/use-width.ts'
import { getTestProps, type TestIds } from '../../utils/testing.ts'
import { Icon, type IconName } from '../icon/icon.tsx'
import type { IconColor, IconFill } from '../icon/types/core.ts'
import {
  type ButtonSize,
  type ButtonThemeColor,
  type ButtonVariant,
  buttonSizeNames,
  buttonThemeColorNames,
  buttonVariantNames,
  getIconSize,
  type UseButtonStylesProps,
  useButtonStyles,
} from './styles.ts'

type ButtonAttributes = HTMLMotionProps<'button'>

type ButtonProps = Readonly<
  PropsWithChildren<
    UseWidthProps &
      Partial<UseButtonStylesProps> &
      ButtonAttributes &
      TestIds &
      Partial<{
        disabled: boolean
        iconLeft: IconName
        iconRight: IconName
        iconFill: IconFill
        iconColor: IconColor
        loading: boolean
        readonly: boolean
        wrap: boolean
      }>
  >
>

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      borderColor,
      children,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      iconColor = 'current',
      iconFill = 'outline',
      iconLeft,
      iconRight,
      loading,
      onClick,
      readonly: readonlyProp,
      shape,
      size = 'md',
      textColor,
      type = 'button',
      variant = 'primary',
      ...props
    },
    ref
  ) => {
    const { buttonClassName } = useButtonStyles({
      size,
      variant,
      shape,
      borderColor,
      textColor,
    })

    const iconSize = getIconSize(size)
    const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (loading) {
        return
      }
      onClick?.(e)
    }

    const readonly = readonlyProp || (type === 'button' && !onClick)
    const isInteractive = !disabled && !loading && !readonly

    return (
      <motion.button
        ref={ref}
        type={type}
        {...props}
        aria-disabled={disabled}
        aria-readonly={readonly}
        aria-busy={loading}
        className={cn(
          'button-base',
          buttonClassName,
          {
            'pointer-events-none': loading,
          },
          className
        )}
        disabled={disabled}
        initial={{ scale: 1 }}
        onClick={handleOnClick}
        whileHover={isInteractive ? { scale: 1.02 } : undefined}
        whileTap={isInteractive ? { scale: 0.98 } : undefined}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        {iconLeft && (
          <Icon
            name={iconLeft}
            size={iconSize}
            fill={iconFill}
            color={iconColor}
            className={cn({ invisible: loading })}
          />
        )}
        {children && (
          <span className={cn({ invisible: loading })}>{children}</span>
        )}
        {loading && !shape && (
          <Icon
            className="absolute"
            color={iconColor}
            name="LoadingDots"
            size="2xl"
          />
        )}
        {iconRight && !shape && (
          <Icon
            name={iconRight}
            size={iconSize}
            fill={iconFill}
            color={iconColor}
            className={cn({ invisible: loading })}
          />
        )}
      </motion.button>
    )
  }
)

export * from './buttons.tsx'
export * from './icon-button.tsx'
export * from './link-button.tsx'
export {
  Button,
  type ButtonAttributes,
  type ButtonProps,
  type ButtonSize,
  type ButtonThemeColor,
  type ButtonVariant,
  buttonSizeNames,
  buttonThemeColorNames,
  buttonVariantNames,
}
